import { ReactNode, useMemo } from "react";

import { IStackProps, mergeStyleSets, Stack, Variant } from "@bps/fluent-ui";

import {
  FieldsetActionButton,
  FieldsetActionButtonProps
} from "./FieldsetActionButton.tsx";
import { FieldsetLegend } from "./FieldsetLegend.tsx";

/**
 * Wraps the Stack component to define a common gap between form fields
 * unless a gap is explicitly provided.
 * Also has an optional legend and actionButton props to specify a legend for the whole group
 * and extra button to do something with the field
 *
 * @param props - IStackProps & { legend?: string, actionButton?: ActionButtonProps, frame?: boolean }
 */

type Children = React.ReactElement[] | React.ReactElement;

const getInputProps = (
  children: Children
): { name: string; disabled?: boolean }[] => {
  const array = Array.isArray(children) ? children : [children];

  return array.reduce(
    (
      acc: { name: string; disabled?: boolean }[],
      child: React.ReactElement
    ) => {
      if (!child?.props) {
        return acc;
      } else if (child.props.name) {
        return [
          ...acc,
          { name: child.props.name, disabled: child.props.disabled }
        ];
      } else if (child.props.children) {
        return [...acc, ...getInputProps(child.props.children)];
      }

      return acc;
    },
    []
  );
};

type FieldsetProps = IStackProps & {
  legend?: ReactNode;
  legendLevel?: Variant;
  actionButton?: FieldsetActionButtonProps;
  frame?: boolean;
  hasAsterisk?: boolean;
};

export const Fieldset: React.FunctionComponent<FieldsetProps> = (
  props: FieldsetProps
) => {
  const {
    legend,
    children,
    actionButton,
    hasAsterisk,
    legendLevel,
    styles,
    ...rest
  } = props;

  // get all name props from children, and prevent the recalling by using useMemo
  const inputProps = useMemo(
    () => (legend && children ? getInputProps(children as Children) : []),
    [children, legend]
  );

  const tokens: IStackProps["tokens"] =
    typeof props.tokens === "function"
      ? props.tokens
      : {
          childrenGap: 8,
          ...props.tokens
        };

  return (
    <Stack
      as="fieldset"
      styles={(_prop, theme) =>
        mergeStyleSets(
          {
            root: {
              minWidth: "auto"
            }
          },
          {
            root: props.frame && {
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: theme.palette.neutralLight,
              borderRadius: "2px",
              padding: 8
            }
          },
          props && styles
        )
      }
    >
      {children && (legend || actionButton) && (
        <Stack
          horizontal
          horizontalAlign="space-between"
          styles={{ root: { padding: "5px 0" } }}
        >
          {legend && (
            <FieldsetLegend
              legend={legend}
              inputProps={inputProps}
              legendLevel={legendLevel}
              hasAsterisk={hasAsterisk}
              styles={styles}
            />
          )}
          {actionButton && <FieldsetActionButton {...actionButton} />}
        </Stack>
      )}

      <Stack tokens={tokens} {...rest}>
        {children}
      </Stack>
    </Stack>
  );
};
